/**
 * AdminHeroImages — the picture and words at the top of every public page,
 * changeable without touching code.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MediaUpload } from '@/components/MediaUpload';
import { SiteBrandCard } from '@/components/admin/SiteBrandCard';
import { ArrowLeft, Loader2, RotateCcw, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const full = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

const HeroRow = ({ hero }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(hero);
  React.useEffect(() => setForm(hero), [hero]);
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === 'video_url') next.media_priority = (v || '').trim() ? 'video' : 'image';
    if (k === 'image_url' && (v || '').trim()) next.media_priority = 'image';
    return next;
  });
  const dirty = ['image_url', 'video_url', 'eyebrow', 'title', 'accent', 'blurb']
    .some((f) => (form[f] || '') !== (hero[f] || ''));
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-heroes'] });
    queryClient.invalidateQueries({ queryKey: ['public-heroes'] });
  };

  const save = useMutation({
    mutationFn: async () => (await api.put(`/admin/public-heroes/${hero.key}`, {
      image_url: form.image_url, video_url: form.video_url,
      eyebrow: form.eyebrow, title: form.title,
      accent: form.accent, blurb: form.blurb,
    })).data,
    onSuccess: () => { toast.success(`${hero.module} hero saved`); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post(`/admin/public-heroes/${hero.key}/reset`)).data,
    onSuccess: () => { toast.success('Back to the original'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card data-testid={`hero-card-${hero.key}`}>
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{hero.module}</CardTitle>
          <Badge variant="outline" className="text-[10px] font-mono">{hero.route}</Badge>
          {hero.customised && (
            <Badge variant="secondary" className="text-[10px]">changed here</Badge>
          )}
        </div>
        <CardDescription>
          {hero.updated_at
            ? `Last changed by ${hero.updated_by} on ${new Date(hero.updated_at).toLocaleDateString()}`
            : 'Still showing the picture the app ships with'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-3">
          <div className="aspect-[16/10] overflow-hidden rounded-lg bg-slate-900">
            {form.video_url && form.media_priority === 'video' ? (
              <video src={full(form.video_url)} poster={full(form.image_url)}
                     data-testid={`hero-preview-video-${hero.key}`}
                     autoPlay muted loop playsInline
                     className="h-full w-full object-cover" />
            ) : form.image_url ? (
              <img src={full(form.image_url)} alt={hero.module}
                   data-testid={`hero-preview-${hero.key}`}
                   className="h-full w-full object-cover" />
            ) : null}
          </div>
          {hero.text_editable !== false && (
            <p className="text-[11px] text-slate-500" data-testid={`hero-showing-${hero.key}`}>
              Showing the {form.media_priority === 'video' && form.video_url
                ? 'video (it loops silently on tablet and desktop; phones see the image)'
                : 'image'} — whichever you set last wins
            </p>
          )}
          <MediaUpload value={form.image_url || ''} purpose="hero"
                       label={hero.text_editable === false ? 'Picture' : 'Hero photograph'}
                       testId={`hero-image-${hero.key}`}
                       onChange={(url) => set('image_url', url)} />
          {hero.text_editable !== false && (
          <MediaUpload value={form.video_url || ''} purpose="video"
                       label="Background video (optional)"
                       hint="Loops silently on tablet and desktop; phones see the image"
                       testId={`hero-video-${hero.key}`}
                       onChange={(url) => set('video_url', url)} />
          )}
        </div>

        <div className="lg:col-span-8 space-y-4">
          {hero.text_editable === false ? (
            <p className="text-sm text-slate-500" data-testid={`hero-no-text-${hero.key}`}>
              This picture sits inside the page and carries no words of its own —
              upload a replacement and press Save.
            </p>
          ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Small label above the headline</Label>
              <Input value={form.eyebrow || ''} data-testid={`hero-eyebrow-${hero.key}`}
                     onChange={(e) => set('eyebrow', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Headline</Label>
              <Input value={form.title || ''} data-testid={`hero-title-${hero.key}`}
                     onChange={(e) => set('title', e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Second line, shown in gold</Label>
              <Input value={form.accent || ''} data-testid={`hero-accent-${hero.key}`}
                     onChange={(e) => set('accent', e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Subtitle</Label>
            <Textarea rows={3} value={form.blurb || ''} data-testid={`hero-blurb-${hero.key}`}
                      onChange={(e) => set('blurb', e.target.value)} />
          </div>
          </>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="ghost" size="sm" disabled={!hero.customised}
                    data-testid={`hero-reset-${hero.key}`}
                    onClick={() => reset.mutate()}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Back to the original
            </Button>
            <Button size="sm" disabled={!dirty || save.isPending}
                    data-testid={`hero-save-${hero.key}`}
                    onClick={() => save.mutate()}>
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/** The hero manager on its own, so it can live inside Settings → Appearance
 *  as well as on its own page. */
export const SiteAppearancePanel = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-heroes'],
    queryFn: async () => (await api.get('/admin/public-heroes')).data,
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  return (
    <div className="space-y-6" data-testid="site-appearance-panel">
      <SiteBrandCard />
      {(data?.heroes || []).map((hero) => <HeroRow key={hero.key} hero={hero} />)}
    </div>
  );
};

const AdminHeroImages = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10" data-testid="admin-heroes">
      <div className="mx-auto max-w-[1180px] space-y-6">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}
                  data-testid="heroes-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Admin
          </Button>
          <h1 className="mt-3 text-2xl font-semibold text-slate-900">Site appearance</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            The brand colour and crest, plus the photograph, background video and words a visitor
            sees first on each public page — including the homepage before anyone logs in.
            Changes go live immediately.
          </p>
        </div>
        <SiteAppearancePanel />
      </div>
    </div>
  );
};

export default AdminHeroImages;
